import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  SegmentedControl,
  Select,
  toast,
  useReturnOrigin,
  withReturn,
} from '../../../components/ui';
import { ApiError } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDate, formatMoney, formatMoneySigned } from '../../../lib/format';
import { useCreateInvoiceSchedule } from '../api';
import { todayIso } from '../line-editor';
import { ClientAgreementFields, useClientAgreements, useInvoiceTarget } from './ClientAgreementFields';
import {
  MAX_INSTALLMENTS,
  SPACING_OPTIONS,
  TERMS_OPTIONS,
  addDays,
  buildSchedule,
  toCents,
  type ScheduleRow,
  type Spacing,
} from './schedule';

type Field = 'client' | 'agreement' | 'total' | 'count' | 'rows' | null;

/**
 * A PROJECT BILLED IN INSTALLMENTS (Jesse, 2026-09-30: *"client (or new
 * client), new agreement, and then the total amount, and how many invoices
 * will be sent… then be able to set the times those invoices will be
 * spaced… and the system splits up the total"*).
 *
 * The total is split evenly (odd cents on the last invoice) across dates
 * spaced from the first; every row stays editable before anything is written,
 * and the footing says at all times whether the rows still add up to the
 * total — the server refuses them if they do not. One click creates every
 * draft in one transaction, numbered and dated, each subject reading
 * "<agreement> — Invoice i of N". Future-dated drafts count in the cash
 * forecast at their due dates, which is the point of scheduling them.
 */
export function InstallmentsTab() {
  const navigate = useNavigate();
  const from = useReturnOrigin();
  const schedule = useCreateInvoiceSchedule();
  const { clientId, agreementId } = useInvoiceTarget();
  const agreements = useClientAgreements(clientId);
  const agreement = agreements.data?.agreements.find((a) => a.id === agreementId) ?? null;

  const [total, setTotal] = useState('');
  const [count, setCount] = useState('3');
  const [first, setFirst] = useState(todayIso());
  const [spacing, setSpacing] = useState<Spacing>('monthly');
  const [terms, setTerms] = useState('30');
  const [subject, setSubject] = useState('');
  const [rows, setRows] = useState<ScheduleRow[]>([]);
  const [edited, setEdited] = useState(false);
  const [error, setError] = useState<{ field: Field; message: string } | null>(null);

  const totalCents = toCents(total);
  const countNum = Number(count);
  const countOk = Number.isInteger(countNum) && countNum >= 1 && countNum <= MAX_INSTALLMENTS;

  // The split follows the four inputs that define it; editing a row by hand
  // holds until one of them changes (or "Split evenly again").
  const regenerate = () => {
    setRows(
      totalCents && totalCents > 0 && countOk && first
        ? buildSchedule({ totalCents, count: countNum, first, spacing })
        : [],
    );
    setEdited(false);
  };
  useEffect(regenerate, [totalCents, countNum, first, spacing]); // eslint-disable-line react-hooks/exhaustive-deps

  // A fixed-fee agreement already says what the work is worth: offer what is
  // left of it, once per agreement, and only into an empty box.
  const remaining =
    agreement && agreement.budgetAmount !== null ? agreement.budgetAmount - agreement.metrics.invoiced : null;
  const prefilledFor = useRef('');
  useEffect(() => {
    if (!agreement || prefilledFor.current === agreement.id) return;
    prefilledFor.current = agreement.id;
    if (!total && remaining !== null && remaining > 0) setTotal(remaining.toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement]);

  const sumCents = useMemo(() => rows.reduce((s, r) => s + (toCents(r.amount) ?? 0), 0), [rows]);
  const diffCents = totalCents !== null ? sumCents - totalCents : 0;
  const termsDays = Number(terms);
  const dueOf = (issue: string) => (issue ? addDays(issue, termsDays) : '');

  const editRow = (i: number, patch: Partial<ScheduleRow>) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setEdited(true);
    if (error?.field === 'rows') setError(null);
  };

  const fail = (field: Field, message: string) => {
    setError({ field, message });
    const el = document.querySelector<HTMLElement>(`[data-draft-field="${field}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.querySelector<HTMLElement>('select, input')?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) return fail('client', 'Choose the client — or create one.');
    if (!agreementId) return fail('agreement', 'Choose the agreement these invoices bill — or create one.');
    if (totalCents === null || totalCents <= 0) return fail('total', 'Enter the total, to the cent.');
    if (!countOk) return fail('count', `Between 1 and ${MAX_INSTALLMENTS} invoices.`);
    const bad = rows.findIndex((r) => !r.issueDate || (toCents(r.amount) ?? 0) <= 0);
    if (bad >= 0) return fail('rows', `Invoice ${bad + 1} needs a date and a positive amount.`);
    if (diffCents !== 0) {
      return fail(
        'rows',
        `The invoices add up to ${formatMoney(sumCents / 100)}, not ${formatMoney(totalCents / 100)} — adjust a row or split evenly again.`,
      );
    }
    setError(null);
    try {
      const result = await schedule.mutateAsync({
        clientId,
        agreementId,
        total: totalCents / 100,
        subject: subject.trim() || undefined,
        installments: rows.map((r) => ({
          issueDate: r.issueDate,
          dueDate: dueOf(r.issueDate),
          amount: (toCents(r.amount) ?? 0) / 100,
        })),
      });
      const numbers = result.invoices.map((i) => `#${i.number}`);
      toast.success(
        result.count === 1
          ? `Draft ${numbers[0]} created.`
          : `${result.count} draft invoices created — ${numbers[0]} to ${numbers[numbers.length - 1]}. Nothing has been sent.`,
      );
      navigate(withReturn(`/clients/${clientId}?tab=invoices`, from));
    } catch (err) {
      setError({ field: null, message: err instanceof ApiError ? err.message : 'Something went wrong — try again.' });
    }
  };

  const fieldError = (f: Field) => (error?.field === f ? error.message : undefined);
  const base = subject.trim() || agreement?.name || 'Agreement name';

  const agreementHint = agreement ? (
    <span className="num">
      {agreement.budgetAmount !== null
        ? `${formatMoney(agreement.budgetAmount, { whole: true })} contract · ${formatMoney(agreement.metrics.invoiced, { whole: true })} invoiced so far`
        : `${formatMoney(agreement.metrics.invoiced, { whole: true })} invoiced so far · no contract value set`}
    </span>
  ) : null;

  return (
    <Card>
      <CardHeader
        title="Project installments"
        subtitle="One total, split into a series of draft invoices on the dates you choose."
      />
      <form onSubmit={submit} noValidate>
        <CardBody className="space-y-5">
          <ClientAgreementFields
            preferTypes={['PROJECT']}
            newAgreementDefaults={() => ({
              type: 'PROJECT',
              billBy: 'FIXED',
              ...(totalCents ? { budgetAmount: (totalCents / 100).toFixed(2) } : {}),
              startDate: first,
            })}
            errors={{ client: fieldError('client'), agreement: fieldError('agreement') }}
            onCleared={() => setError(null)}
            agreementHint={agreementHint}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div data-draft-field="total">
              <Input
                label="Total amount ($)"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="e.g. 42000"
                value={total}
                error={fieldError('total')}
                onChange={(e) => {
                  setTotal(e.target.value);
                  if (error?.field === 'total') setError(null);
                }}
              />
            </div>
            <div data-draft-field="count">
              <Input
                label="Number of invoices"
                type="number"
                inputMode="numeric"
                min="1"
                max={MAX_INSTALLMENTS}
                step="1"
                value={count}
                error={fieldError('count')}
                onChange={(e) => {
                  setCount(e.target.value);
                  if (error?.field === 'count') setError(null);
                }}
              />
            </div>
            <Input
              label="First invoice date"
              type="date"
              value={first}
              onChange={(e) => setFirst(e.target.value || todayIso())}
            />
            <Select
              label="Payment terms"
              value={terms}
              options={TERMS_OPTIONS}
              onChange={(e) => setTerms(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <div>
              <span className="mb-1.5 block text-body-sm font-semibold text-secondary">Spaced</span>
              <SegmentedControl
                ariaLabel="How far apart the invoices are"
                value={spacing}
                onChange={setSpacing}
                options={SPACING_OPTIONS}
              />
            </div>
            <Input
              label="Invoice subject"
              className="min-w-[16rem] flex-1"
              placeholder={agreement?.name ?? 'Defaults to the agreement name'}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              hint={`Each reads “${base} — Invoice 1 of ${countOk ? countNum : 'N'}”.`}
            />
          </div>
        </CardBody>

        <div className="border-t border-grey-200" data-draft-field="rows">
          {rows.length === 0 ? (
            <p className="px-6 py-5 text-body-sm text-grey-500">
              Enter a total and the number of invoices to see the schedule.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="border-b border-grey-200 text-left text-caption font-bold uppercase tracking-wider text-grey-500">
                    <th className="px-3 py-2.5 sm:pl-6 sm:pr-4">Invoice</th>
                    <th className="px-1 py-2.5 sm:px-2">Issue date</th>
                    <th className="hidden px-2 py-2.5 sm:table-cell">Due</th>
                    <th className="px-3 py-2.5 text-right sm:pl-4 sm:pr-6">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-grey-100">
                      <td className="num whitespace-nowrap px-3 py-2 font-semibold text-secondary sm:pl-6 sm:pr-4">
                        {i + 1} of {rows.length}
                      </td>
                      <td className="px-1 py-2 sm:px-2">
                        <input
                          type="date"
                          aria-label={`Invoice ${i + 1} issue date`}
                          value={row.issueDate}
                          onChange={(e) => editRow(i, { issueDate: e.target.value })}
                          className="num w-[8.5rem] rounded-md border border-grey-300 px-1.5 py-1 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 sm:w-auto sm:px-2"
                        />
                        <span className="num mt-0.5 block text-caption text-grey-500 sm:hidden">
                          due {row.issueDate ? (termsDays === 0 ? 'on receipt' : formatDate(dueOf(row.issueDate))) : '—'}
                        </span>
                      </td>
                      <td className="num hidden whitespace-nowrap px-2 py-2 text-grey-500 sm:table-cell">
                        {row.issueDate ? (termsDays === 0 ? 'on receipt' : formatDate(dueOf(row.issueDate))) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right sm:pl-4 sm:pr-6">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Invoice ${i + 1} amount`}
                          value={row.amount}
                          onChange={(e) => editRow(i, { amount: e.target.value })}
                          className={cn(
                            'num w-24 rounded-md border px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 sm:w-32',
                            toCents(row.amount) === null || (toCents(row.amount) ?? 0) <= 0
                              ? 'border-error focus:ring-error/20'
                              : 'border-grey-300 focus:border-accent focus:ring-accent/20',
                          )}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-body-sm">
                    <td className="px-3 py-3 sm:pl-6 sm:pr-4">
                      {edited && (
                        <button
                          type="button"
                          onClick={regenerate}
                          className="inline-flex items-center gap-1.5 font-semibold text-grey-500 hover:text-secondary"
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                          Split evenly again
                        </button>
                      )}
                    </td>
                    <td className="hidden sm:table-cell" />
                    <td className="px-2 py-3 text-right font-semibold text-grey-500">Total</td>
                    <td className="num whitespace-nowrap px-3 py-3 text-right font-bold text-secondary sm:pl-4 sm:pr-6">
                      {formatMoney(sumCents / 100)}
                      {diffCents !== 0 && totalCents !== null && (
                        <span className="block text-caption font-semibold text-status-late">
                          {formatMoneySigned(diffCents / 100)} vs {formatMoney(totalCents / 100)}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <CardBody className="border-t border-grey-200 pt-4">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {error && (error.field === null || error.field === 'rows') && (
              <p role="alert" className="mr-auto flex items-center gap-1.5 text-body-sm font-semibold text-error">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                {error.message}
              </p>
            )}
            <Button type="submit" size="lg" loading={schedule.isPending} disabled={rows.length === 0}>
              {rows.length > 1 ? `Create ${rows.length} draft invoices` : 'Create draft invoice'}
            </Button>
          </div>
        </CardBody>
      </form>
    </Card>
  );
}
